import type { PreOrderFlow } from "@/app/api/types/preOrder";
import type { UnitResponse } from "@/app/api/types/unit";
import { readComputeTimeWeightOverride } from "@/lib/products/computeTimeWeightOverride";

export interface FlowTotals {
  totalWeight: number;
  totalQuantity: number;
  totalPrice: number;
}

export interface ProductTotals extends FlowTotals {
  productId: string;
}

export function calculateFlowWeight(
  flow: PreOrderFlow,
  units: UnitResponse[],
): number {
  // Compute-time weight override: for packaging-reference products (e.g. oyster
  // bourriches) the `weight` cell holds a colisage count, not kg. When the
  // product's metadata carries an override, the real line weight is
  // quantity × override, bypassing the cell entirely.
  const override = readComputeTimeWeightOverride(
    flow.product?.product_metadata,
  );
  if (override != null) return (flow.quantity || 0) * override;

  if (flow.weight != null && flow.weight > 0) return flow.weight;

  const quantity = flow.quantity || 0;
  if (quantity === 0) return 0;

  const unit = units.find((u) => u.id === flow.unit_id);
  if (!unit) return 0;

  // Check junction weight (product_units.weight) — overrides product-level weight
  const junctionWeight = flow.product?.units?.find(
    (u) => u.unit_id === unit.id,
  )?.weight;
  if (junctionWeight != null && junctionWeight > 0) {
    return unit.per_box ? junctionWeight : quantity * junctionWeight;
  }

  if (unit.per_box && flow.product?.box_weight)
    return quantity * flow.product.box_weight;
  if (!unit.per_box && flow.product?.piece_weight)
    return quantity * flow.product.piece_weight;
  return 0;
}

/**
 * Unit semantics are read from `flow.unit` (the embedded object). When a flow
 * carries only `flow.unit_id`, pass `units` so we can look it up — otherwise
 * `per_box` / `price_per_unit` would be treated as falsy and totals would
 * silently fall back to plain weight × price.
 */
function resolveUnit(
  flow: PreOrderFlow,
  units?: UnitResponse[],
): UnitResponse | null | undefined {
  if (flow.unit) return flow.unit;
  if (!flow.unit_id || !units?.length) return null;
  return units.find((u) => u.id === flow.unit_id) ?? null;
}

/**
 * Display weight for a flow — respects per-box units where `flow.weight` is
 * stored per box. Does not derive from quantity × unit; use calculateFlowWeight
 * for that. Prefers `flow.unit`; pass `units` to resolve from `flow.unit_id`
 * when the embedded object is missing.
 */
export function calculateFlowDisplayWeight(
  flow: PreOrderFlow,
  units?: UnitResponse[],
): number {
  const quantity = flow.quantity || 0;
  // Compute-time weight override (see `calculateFlowWeight`): the product's
  // real per-unit kg wins over the colisage-bearing `weight` cell. Lives here
  // so every footer/stat total — and weight-based price via `calculateFlowTotal`
  // — uses real kilograms while the per-row WeightCell still shows the cell.
  const override = readComputeTimeWeightOverride(
    flow.product?.product_metadata,
  );
  if (override != null) return quantity * override;

  const weight = flow.weight || 0;
  const unit = resolveUnit(flow, units);
  return unit?.per_box ? weight * quantity : weight;
}

/**
 * Per-flow monetary total. Pricing semantics come from the flow's unit config:
 * - price_per_unit: quantity × price (e.g. 10 boxes × €5/box = €50)
 * - per_box (weight-based): (weight × quantity) × price
 * - default (weight-based): weight × price
 *
 * Single source of truth — all recap aggregations delegate here. Prefers
 * `flow.unit`; pass `units` to resolve from `flow.unit_id` when the embedded
 * object is missing — otherwise totals silently fall back to weight-based.
 *
 * Optional `previewPrice`: phantom value the PriceCell uses when the user
 * hasn't committed a price. Callers that have a price resolver in scope
 * pass it so totals include the preview — keeping "what the row shows"
 * and "what the footer sums" in sync.
 *
 * Both `null` AND `0` for `flow.price_per_weight` count as "no commit"
 * here — legacy DB rows have `0` default-stamped and the recap UI demotes
 * them to the phantom preview path in lockstep (PriceCell / TotalCell).
 * A genuinely committed non-zero price always wins.
 */
export function calculateFlowTotal(
  flow: PreOrderFlow,
  units?: UnitResponse[],
  previewPrice?: number | null,
): number {
  const committed =
    flow.price_per_weight != null && flow.price_per_weight !== 0
      ? flow.price_per_weight
      : null;
  const price = committed != null ? committed : (previewPrice ?? 0);
  const quantity = flow.quantity || 0;
  const unit = resolveUnit(flow, units);
  if (unit?.price_per_unit) return quantity * price;
  return calculateFlowDisplayWeight(flow, units) * price;
}

export function calculateEffectivePrice(
  flow: PreOrderFlow,
  partnerPrices: Map<string, number>,
): number {
  if (!flow.product_id) return 0;
  const partnerPrice = partnerPrices.get(flow.product_id);
  if (partnerPrice !== undefined) return partnerPrice;
  return flow.product?.weight_price ?? 0;
}

export function calculateTotals(
  flows: PreOrderFlow[],
  units: UnitResponse[],
): FlowTotals {
  return {
    totalWeight: flows.reduce(
      (sum, f) => sum + calculateFlowWeight(f, units),
      0,
    ),
    totalQuantity: flows.reduce((sum, f) => sum + (f.quantity || 0), 0),
    totalPrice: flows.reduce((sum, f) => sum + calculateFlowTotal(f), 0),
  };
}

export function calculateProductTotals(
  flows: PreOrderFlow[],
  units: UnitResponse[],
): ProductTotals[] {
  const productMap = new Map<string, PreOrderFlow[]>();
  for (const flow of flows) {
    if (!flow.product_id) continue;
    const existing = productMap.get(flow.product_id) || [];
    productMap.set(flow.product_id, [...existing, flow]);
  }

  return Array.from(productMap.entries()).map(([productId, productFlows]) => ({
    productId,
    ...calculateTotals(productFlows, units),
  }));
}

/** Status counts from flow aggregation. */
export interface StatusCounts {
  pending: number;
  confirmed: number;
  flagged: number;
  cancelled: number;
}

/** Aggregated stats for a set of flows (used by PartnerGroup, PreOrderHeader, ProductGroup, FlowsTable). */
export interface FlowStats {
  totalWeight: number;
  totalAmount: number;
  quantitiesByUnit: Record<string, number>;
  statusCounts: StatusCounts;
}

/**
 * Compute weight, amount, quantities-by-unit and status counts from a flat
 * list of flows. Pass `units` to resolve `flow.unit_id` when `flow.unit` is
 * not populated — otherwise pricing/weight semantics silently fall back to
 * plain weight-based.
 *
 * Optional `getPreviewPrice(flow)`: resolver for the phantom price the
 * PriceCell displays when the user hasn't committed a price. Passing it
 * keeps the partner/product totals in sync with the per-row preview. The
 * recap mounts the resolver via `ProductLookupsContext`; callers without
 * one (PDF generation, legacy paths) omit it and stay strictly committed-
 * price.
 */
export function computeFlowStats(
  flows: PreOrderFlow[],
  units?: UnitResponse[],
  getPreviewPrice?: (flow: PreOrderFlow) => number | null,
  /**
   * Label bucket for quantities whose flow carries no resolvable unit.
   * Defaults to `"units"`. Transport passes a localized `"colis"`, sales/
   * purchases a localized `"unité"`, so unit-less lines aggregate + display
   * under that label instead.
   */
  noUnitLabel = "units",
  /**
   * When the unit column is disabled (transport, or `showUnitRecap=false`),
   * quantities are meaningless per-unit — bucket EVERY flow under
   * `noUnitLabel` regardless of its resolved unit, so the footer/header/row
   * totals read as a single "colis"/"unité" figure.
   */
  unitsDisabled = false,
): FlowStats {
  let totalWeight = 0;
  let totalAmount = 0;
  const quantitiesByUnit: Record<string, number> = {};
  const statusCounts: StatusCounts = {
    pending: 0,
    confirmed: 0,
    flagged: 0,
    cancelled: 0,
  };

  for (const flow of flows) {
    const resolvedAbbr = unitsDisabled
      ? noUnitLabel
      : flow.unit?.abbreviation ||
        (flow.unit_id && units
          ? units.find((u) => u.id === flow.unit_id)?.abbreviation
          : undefined) ||
        noUnitLabel;
    const quantity = flow.quantity || 0;

    totalWeight += calculateFlowDisplayWeight(flow, units);
    quantitiesByUnit[resolvedAbbr] =
      (quantitiesByUnit[resolvedAbbr] || 0) + quantity;
    totalAmount += calculateFlowTotal(flow, units, getPreviewPrice?.(flow));

    const status = flow.status ?? 0;
    if (status === 0) statusCounts.pending++;
    else if (status === 1) statusCounts.confirmed++;
    else if (status === 2) statusCounts.flagged++;
    else if (status === 3) statusCounts.cancelled++;
  }

  return {
    totalWeight: Math.round(totalWeight * 100) / 100,
    totalAmount: Math.round(totalAmount * 100) / 100,
    quantitiesByUnit,
    statusCounts,
  };
}

export function calculateWeight(
  quantity: number,
  unit: UnitResponse | null | undefined,
  product: {
    box_weight?: number | null;
    piece_weight?: number | null;
    units?: { unit_id: string; weight?: number | null }[] | null;
  } | null,
): number {
  if (!quantity || !unit || !product) return 0;

  // Check junction weight (product_units.weight) — overrides product-level weight
  const junctionWeight = product.units?.find(
    (u) => u.unit_id === unit.id,
  )?.weight;
  if (junctionWeight != null && junctionWeight > 0) {
    return unit.per_box ? junctionWeight : quantity * junctionWeight;
  }

  if (unit.per_box && product.box_weight) return quantity * product.box_weight;
  if (!unit.per_box && product.piece_weight)
    return quantity * product.piece_weight;
  return 0;
}

export function formatPrice(
  price: number,
  currency = "€",
  decimals = 2,
): string {
  return `${price.toFixed(decimals)}${currency}`;
}

export function formatWeight(
  weight: number,
  unit = "kg",
  decimals = 2,
): string {
  return `${weight.toFixed(decimals)}${unit}`;
}
